import './App.css';
import React, {useEffect, useState} from 'react';
import {getAuth, onAuthStateChanged} from "@firebase/auth";
import {Route, Routes} from "react-router";
import {Container} from "react-bootstrap";
import LandingPage from "./pages/LandingPage";
import {
  serverTimestamp,
  doc, setDoc, getDocs, where,
  query, collection, onSnapshot, orderBy, limit,
} from "@firebase/firestore";
import {db} from "./firebase/FirebaseProvider";
import {Business, CardTransaction, CurrentUser, UserCard, UserType} from "./types/Types";
import Header from "./components/header/Header";
import LoginPage from "./pages/login/LoginPage";
import RegisterResponse from "./pages/login/RegisterResponse";
import SettingsPage from "./pages/login/SettingsPage";
import RegisterPage from "./pages/login/RegisterPage";
import WalletPage from "./pages/wallet/WalletPage";
import ScannerPage from "./pages/scan/ScannerPage";
import CardPage from "./pages/card/CardPage";
import LocationsPage from "./pages/locations/LocationsPage";
import OffersPage from "./pages/offers/OffersPage";
import LocationPage from "./pages/locations/LocationPage";
import EventsPage from "./pages/events/EventsPage";
import BusinessPage from "./pages/business/BusinessPage";
import MenuPage from "./pages/menu/MenuPage";
import BusinessSettingsPage from "./pages/settings/BusinessSettingsPage";
import {createBrowserHistory} from "history";
import CustomRouter from "./CustomRouter";
import OffersCreationPage from "./pages/offers/configure/OffersCreationPage";
import OfferPage from "./pages/offers/OfferPage";
import MenuSectionCreationPage from "./pages/menu/configure/MenuSectionCreationPage";
import MenuItemCreationPage from "./pages/menu/configure/MenuItemCreationPage";
import MenuSectionUpdatePage from "./pages/menu/configure/MenuSectionUpdatePage";
import MenuItemUpdatePage from "./pages/menu/configure/MenuItemUpdatePage";

function App() {
  const [loading, setLoading] = useState<Boolean>(false);
  const [currentUser, setCurrentUser] = useState<CurrentUser>();
  const [businessMap, setBusinessMap] = useState<Map<string, Business>>();
  const [userCardsMap, setUserCardsMap] = useState<Map<string, UserCard>>(new Map());

  const history = createBrowserHistory();

  const createUserIfDoesNotExist = async (docRef: any, userId: string, email: string) => {
    const usersRef = collection(db, 'users');
    const userCollection = query(usersRef, where('email', '==', email.toLowerCase()));
    const userCollectionSnap = await getDocs(userCollection);
    if(userCollectionSnap.size == 0) {
      const data = {
        lastSeenAt: serverTimestamp(),
        id: userId,
        email: email,
        userType: UserType.CUSTOMER,
        createdAt: new Date(),
      };
      await setDoc(docRef, data, { merge: true });
    }
  };

  useEffect(() => {
    const auth = getAuth();
    onAuthStateChanged(auth, async (userCredential: any | undefined) => {
      if (userCredential && !currentUser) {
        const docRef = doc(db, 'users', userCredential.uid);
        await createUserIfDoesNotExist(docRef, userCredential.uid, userCredential.email);
        const userCollection = query(
            collection(db, 'users'),
            where('id', '==', userCredential.uid),
        );
        const userCollectionSnap = await getDocs(userCollection);
        await userCollectionSnap.forEach((userDoc) => {
          onSnapshot(doc(db, 'users', userDoc.id), (doc) => {
            if (doc.exists() && !currentUser) {
              setCurrentUser(doc.data() as CurrentUser);
              setLoading(false);
            }
          });
        });
      }
    });
  });

  useEffect(() => {(async () => {
    let businesses = new Map<string, Business>();
    const businessesCollection = query(
        collection(db, 'businesses'),
        where("active", "==",true),
        orderBy("name", "desc"),
        limit(200),
    );
    const businessesSnapshot = await getDocs(businessesCollection);
    businessesSnapshot.forEach((doc) => {
      businesses.set(doc.data().id, doc.data() as Business);
    });
    setBusinessMap(businesses);
  })();
  }, [currentUser]);

  useEffect(() => {
    if(!currentUser) {
      return
    }
    onSnapshot(query(
        collection(db, 'cards'),
        where("user", "==", currentUser.id),
        orderBy("createdAt", "desc"),
        limit(1000),
    ), (snapshot) => {
      snapshot.docChanges().forEach(card => {
        if (card.type === 'added' || card.type === 'modified') {
          const cardToSet = card.doc.data() as UserCard;
          userCardsMap.set(cardToSet.business, cardToSet);
        }
      });
    });
  }, [currentUser]);

  return (

      <div style={{minHeight: "100vh", display: "flex", flexDirection: "column", justifyContent: "space-between"}}>

          <div>

            <CustomRouter history={history}>
              <Container className={"mt-2 mb-3 app-container"}>
                <Header currentUser={currentUser} businessMap={businessMap} setCurrentUser={setCurrentUser} loading={currentUser == undefined} />
                <div className={"ms-2 me-2"}>
                  {loading && <div className={"grey"}>loading</div>}
                  {!loading && <Routes>
                    {/* No auth required */}
                      <Route path="/login/auth/response" element={<RegisterResponse redirectTo={""} /> }/>
                      <Route path="/register/auth/response" element={<RegisterResponse redirectTo={"settings"} /> }/>
                      <Route path="/login" element={<LoginPage />} />
                      <Route path="/register" element={<RegisterPage />} />

                    {/* Auth Required */}
                    {currentUser && <>
                      {<Route path="/" element={<LandingPage currentUser={currentUser} />} />}
                      {<Route path="/scan" element={<ScannerPage currentUser={currentUser} /> }/>}
                      {<Route path="/settings" element={<SettingsPage currentUser={currentUser} /> }/>}

                      {businessMap && <>
                        {/*Offers*/}
                        {userCardsMap && <Route path="/offers" element={<OffersPage currentUser={currentUser} businessMap={businessMap} userCardsMap={userCardsMap} />}/>}
                        {<Route path="/offer/:id" element={<OfferPage currentUser={currentUser} businessMap={businessMap} />}/>}
                        {<Route path="/offers/create" element={<OffersCreationPage currentUser={currentUser} business={businessMap.get(currentUser.business || "")} />}/>}

                        {/*Events*/}
                        {<Route path="/events" element={<EventsPage currentUser={currentUser} businessMap={businessMap}/>}/>}

                        {/*Menu*/}
                        {<Route path="/menu" element={<MenuPage currentUser={currentUser} businessMap={businessMap}/>}/>}
                        {<Route path="/menu/create-section" element={<MenuSectionCreationPage currentUser={currentUser} business={businessMap.get(currentUser.business || "")} />}/>}
                        {<Route path="/menu/section/:sectionId/update" element={<MenuSectionUpdatePage currentUser={currentUser} business={businessMap.get(currentUser.business || "")} />}/>}
                        {<Route path="/menu/section/:sectionId/create" element={<MenuItemCreationPage currentUser={currentUser} business={businessMap.get(currentUser.business || "")} />}/>}
                        {<Route path="/menu/section/:sectionId/item/:itemId/update" element={<MenuItemUpdatePage currentUser={currentUser} business={businessMap.get(currentUser.business || "")} />}/>}

                        {/*Locations*/}
                        {userCardsMap && <Route path="/locations" element={<LocationsPage currentUser={currentUser} businessMap={businessMap} userCardsMap={userCardsMap} /> }/>}
                        {userCardsMap && <Route path="/location/:id" element={<LocationPage currentUser={currentUser} businessMap={businessMap} userCardsMap={userCardsMap} />}/>}

                        {/*Wallet and Cards*/}
                        {userCardsMap && <Route path="/wallet" element={<WalletPage currentUser={currentUser} businessMap={businessMap} userCardsMap={userCardsMap}/>}/>}
                        {<Route path="/card/:id" element={<CardPage currentUser={currentUser} businessMap={businessMap}/>}/>}

                        {/*Business*/}
                        {currentUser.userType === UserType.LOCATION_MANAGER && <Route path="/business/:id" element={<BusinessPage currentUser={currentUser} businessMap={businessMap}/>}/>}
                        {<Route path="/business/configure" element={<BusinessSettingsPage currentUser={currentUser} /> }/>}
                      </>}
                    </>}
                  </Routes>}
                </div>
              </Container>
            </CustomRouter>

          </div>


          <footer className={"white"}>
              Footer
          </footer>

      </div>
  );
}

export default App;
